/**
 * navTree.ts — single source of truth for the mega-menu navigation.
 *
 * Shape: DOMAIN → COLUMN[] → ITEM[]. Each item carries a `require` gate key
 * resolved by the SHARED `userCanAccess()` in menuConfig.js (do NOT duplicate
 * gate logic here). `getVisibleTree()` filters items, then drops empty columns,
 * then drops empty domains — that cascade is what makes each role see a nav
 * tailored to it, with no dead triggers.
 *
 * `notif` keys map to counts produced by useNavAlerts(); an unresolved key
 * simply shows no dot. Desktop MegaMenu + mobile drawer + ⌘K palette all read
 * from here.
 */
import { userCanAccess, instanceAllows } from './menuConfig';
import { INSTANCE } from '../instance';

/**
 * Visible on both deployments. Absent `instances` means Firefleet-only, so this
 * is how a feature opts IN to SwarmDefense.Systems. Allowlist, never denylist:
 * anything shipped from here on stays Firefleet-only until someone decides
 * otherwise. See docs/operations/two-instances.md.
 */
const BOTH = ['firefly', 'swarm'];

/**
 * SwarmDefense.Systems only — the mirror image of the default, and rarer than it
 * looks. Almost everything one-sided is Firefleet-only, which needs no marker at
 * all; this is for the handful of things SwarmSystems has and Firefleet does not.
 *
 * The first of them (`/serialmint`) is why `allowedOn` in routeInstances.ts no
 * longer short-circuits on Firefleet: hiding a nav item does not stop a typed
 * URL, and Firefleet must not be able to reach a Swarm-only page.
 */
const SWARM_ONLY = ['swarm'];

export interface NavItem {
  label: string;
  to: string;
  require?: string;      // gate key for userCanAccess (undefined = always visible)
  instances?: string[];  // deployments that have it (undefined = Firefleet only)
  notif?: string;        // alert key looked up in useNavAlerts()
  desc?: string;         // one-line description shown in the panel row
  icon?: string;         // navIcons key; falls back to the domain icon
  lock?: boolean;        // render a "Financial" lock chip (can_financial surfaces)
}

export interface NavColumn {
  title: string;
  items: NavItem[];
}

export interface NavFeatured {
  eyebrow: string;
  metric?: string;
  sub: string;
  cta: string;
  to: string;
}

export interface NavDomain {
  key: string;
  label: string;
  /**
   * Per-deployment label override. Needed where a domain survives instance
   * gating but its name no longer fits what is left inside it.
   */
  instanceLabel?: Record<string, string>;
  icon: string;       // navIcons key
  featured?: NavFeatured;
  columns: NavColumn[];
}

/** Palette-only destinations: real routes that intentionally live in no menu. */
export interface ExtraDestination {
  label: string;
  to: string;
  require?: string;
  instances?: string[];
  crumb: string;      // which domain it conceptually belongs to
}

/**
 * Both gates, in the order that reads best: does this product have the feature,
 * and may this person see it. Takes the whole item rather than just the gate key
 * because instance visibility is carried on the item too.
 */
const can = (item: { require?: string; instances?: string[] } | undefined, user: unknown): boolean =>
  instanceAllows(item) && userCanAccess(item, user);

export const NAV_TREE: NavDomain[] = [
  {
    key: 'fleet',
    label: 'Fleet',
    icon: 'drone',
    featured: { eyebrow: 'Fleet', metric: '', sub: 'Drones, chargers & gear inventory', cta: 'View drones', to: '/fleet' },
    columns: [
      {
        title: 'Inventory',
        items: [
          { label: 'Drones', to: '/fleet', instances: BOTH, desc: 'Gen 2 inventory & status', icon: 'drone' },
          { label: 'Chargers', to: '/charge', require: 'chargeControl', desc: 'Charger fleet control & assets', icon: 'charge' },
          { label: 'Assets', to: '/assets', instances: BOTH, require: 'assetsRead', desc: 'TMs, RTKs, kits & gear', icon: 'asset' },
          { label: 'FAA Registrations', to: '/faaregistrations', instances: BOTH, desc: 'Drone registration status', icon: 'catalog' },
          { label: 'Mint Drone', to: '/serialmint', instances: SWARM_ONLY, require: 'manufacturing', desc: 'Hand-mint a serial and Pi hostname', icon: 'drone' },
        ],
      },
    ],
  },
  {
    key: 'warehouse',
    label: 'Warehouse',
    icon: 'warehouse',
    featured: { eyebrow: 'Receiving', metric: '', sub: 'Stock, receiving & procurement', cta: 'Open receiving hub', to: '/receiving' },
    columns: [
      {
        title: 'Stock',
        items: [
          { label: 'Inventory', to: '/inventory', instances: BOTH, require: 'catalogRead', notif: 'lowparts', desc: 'Stock by SKU & location', icon: 'box' },
          // Catalog carries its own sub-tools ON THE PAGE: "From a document" and
          // "Part families". Both were menu items and both were the wrong shape
          // for one — you reach for them BECAUSE you are looking at the catalog,
          // and a menu that lists every screen stops being a list of PLACES.
          // Still ⌘K-reachable; see EXTRA_DESTINATIONS.
          { label: 'Catalog', to: '/parts-catalog', instances: BOTH, require: 'catalogRead', desc: 'SKU master list', icon: 'catalog' },
          { label: 'Product BOMs', to: '/parts/boms', instances: BOTH, require: 'catalogRead', desc: 'Parts lists and build capacity for every product', icon: 'drone' },
          { label: 'Pulls', to: '/parts/pulls', instances: BOTH, require: 'pulls', desc: 'R&D, repair and scrap pulls by project', icon: 'box' },
          { label: 'Scrap bin', to: '/parts/scrap-bin', instances: BOTH, require: 'shopFloor', desc: 'Parts set aside to throw out or harvest', icon: 'box' },
          { label: 'Locations', to: '/admin/locations', instances: BOTH, require: 'admin', desc: 'Where stock can be', icon: 'warehouse' },
          { label: 'Vendors', to: '/admin/vendors', instances: BOTH, require: 'procurement', desc: 'Vendor master & holds', icon: 'catalog' },
        ],
      },
      {
        title: 'Flow',
        items: [
          // Receiving carries "Read a packing list" and "Shipments" on the page;
          // Purchase Orders carries "Import history", "Quote to PO" and "Scan PO".
          // Same rule as Catalog above: a tool you reach for while looking at a
          // screen belongs ON that screen, not beside it in the menu.
          { label: 'Receiving', to: '/receiving', instances: BOTH, require: 'receivingHub', desc: 'Boxes & expected receipts', icon: 'box' },
          { label: 'Purchase Orders', to: '/procurement', instances: BOTH, require: 'procurement', notif: 'approvals', desc: 'Vendor orders & approvals', icon: 'order' },
          // Returns carries "Vendor RMA" on the page. Same rule as Receiving and
          // Purchase Orders above: a tool you reach for while looking at a screen
          // belongs ON that screen, not beside it in the menu. Two entries
          // reading "Returns" and "Vendor RMA" would also make the reader choose
          // between them before knowing the difference.
          { label: 'Returns', to: '/procurement/returns', instances: BOTH, require: 'procurement', desc: 'Rejected stock, credits owed & vendor RMAs', icon: 'order' },
          { label: 'Bill Matching', to: '/procurement/match-board', instances: BOTH, require: 'procurement', desc: 'Unbilled order lines & unmatched invoices', icon: 'order' },
          // …EXCEPT for a manufacturing tech with no procurement, who cannot open
          // the Returns page at all and would otherwise have no way in. This entry
          // is exclusive to them (see `vendorRmaDirect` in menuConfig.js), so
          // nobody ever sees both it and Returns.
          { label: 'Vendor RMA', to: '/procurement/board-returns', instances: BOTH, require: 'vendorRmaDirect', desc: 'Send failed boards back', icon: 'box' },
          { label: 'Financial Logs', to: '/financial-logs', instances: BOTH, require: 'financial', lock: true, desc: 'Cost & price audit', icon: 'fin' },
        ],
      },
      {
        title: 'Manufacturing',
        items: [
          { label: 'Work orders', to: '/work-orders', instances: BOTH, require: 'workOrders', notif: 'workOrders', desc: 'Runs, picks and completion', icon: 'order' },
          { label: 'Pick list', to: '/pick-list', instances: BOTH, require: 'workOrders', notif: 'workOrderPicks', desc: 'What needs to move to Assembly', icon: 'box' },
        ],
      },
    ],
  },
  {
    key: 'shows',
    label: 'Shows',
    // On Swarm the Events and Crew columns gate away entirely, leaving only
    // Dispatch and Vehicles — "Shows" would name a domain for a product that
    // has none.
    instanceLabel: { swarm: 'Logistics' },
    icon: 'formation',
    featured: { eyebrow: 'Production', metric: '', sub: 'Events, crew & show logistics', cta: 'View events', to: '/events' },
    columns: [
      {
        title: 'Events',
        items: [
          { label: 'Events', to: '/events', require: 'hasRole', notif: 'monday', desc: 'All shows & tours', icon: 'shows' },
          { label: 'Calendar', to: '/calendar', require: 'hasRole', desc: 'Month & agenda view', icon: 'cal' },
          { label: 'Tour Timeline', to: '/tour-timeline', require: 'hasRole', desc: 'Vehicle-centric grid', icon: 'tour' },
        ],
      },
      {
        title: 'Creative',
        items: [
          // Firefleet only: no `instances` key (docs/operations/instance-exclusives.md).
          { label: 'Spark', to: '/spark', require: 'spark', desc: 'Creative Production Manager', icon: 'spark' },
        ],
      },
      {
        title: 'Crew',
        items: [
          { label: 'Schedule', to: '/schedule', require: 'producer', desc: 'Staff scheduling grid', icon: 'sched' },
          { label: 'Roster', to: '/roster', require: 'producer', notif: 'crew', desc: 'Crew per event', icon: 'roster' },
          { label: 'Pilots', to: '/pilots', require: 'poa', desc: 'Plan of activities', icon: 'pilot' },
        ],
      },
      {
        title: 'Logistics',
        items: [
          { label: 'Dispatch', to: '/dispatch', instances: BOTH, require: 'hasRole', notif: 'logistics', desc: 'Packing & routing board', icon: 'dispatch' },
          { label: 'Vehicles', to: '/vehicles', instances: BOTH, require: 'hasRole', desc: 'Vehicle fleet', icon: 'vehicle' },
        ],
      },
    ],
  },
  {
    key: 'sales',
    label: 'Sales',
    icon: 'sales',
    featured: { eyebrow: 'Commercial', metric: '', sub: 'Customers, orders, leases & rentals', cta: 'View orders', to: '/orders' },
    columns: [
      {
        title: 'Commercial',
        items: [
          { label: 'Customers', to: '/customers', instances: BOTH, require: 'sales', desc: 'Orgs & relationships', icon: 'cust' },
          { label: 'Orders', to: '/orders', instances: BOTH, require: 'hasRole', notif: 'orders', desc: 'Sales, leases, rentals', icon: 'order' },
          { label: 'Drone Transfers', to: '/customers/transfers', require: 'sales', notif: 'transfers', desc: 'Move drones between orgs', icon: 'transfer' },
        ],
      },
    ],
  },
  {
    key: 'service',
    label: 'Service',
    icon: 'service',
    featured: { eyebrow: 'Returns', metric: '', sub: 'RMA queue & maintenance', cta: 'Open RMA list', to: '/rma' },
    columns: [
      {
        title: 'Returns',
        items: [
          { label: 'RMA List', to: '/rma', instances: BOTH, notif: 'rma', desc: 'Internal RMA management', icon: 'rma' },
          { label: 'Maintenance Logs', to: '/maintenance', instances: BOTH, desc: 'Maintenance entries', icon: 'logs' },
        ],
      },
      {
        title: 'Customer-facing',
        items: [
          { label: 'Public Submit', to: '/rma/submit', instances: BOTH, desc: 'Customer RMA form', icon: 'rma' },
          { label: 'Public Status', to: '/rma/status', instances: BOTH, desc: 'Customer status lookup', icon: 'rma' },
        ],
      },
    ],
  },
  {
    key: 'tools',
    label: 'Tools',
    icon: 'tools',
    featured: { eyebrow: 'Flight analyst', metric: '', sub: 'PX4 log deep dives & fleet reliability', cta: 'Open analyst', to: '/analyst' },
    columns: [
      {
        title: 'Utilities',
        items: [
          { label: 'Map', to: '/map', desc: 'Live drone locations', icon: 'map' },
          { label: 'Firmware', to: '/firmware', instances: BOTH, require: 'firmware', notif: 'firmware', desc: 'Versions & downloads', icon: 'fw' },
          { label: 'Scanner Mode', to: '/scanner', instances: BOTH, desc: 'Assembly & board pairing', icon: 'scan' },
          // Ungated on purpose: it prints stickers and reads no data.
          { label: 'Label Maker', to: '/label-maker', instances: BOTH, desc: 'Print any text or QR', icon: 'label' },
          { label: 'Node Plans', to: '/node-plans', instances: BOTH, desc: 'Shared plans, one topic per node', icon: 'family' },
        ],
      },
      {
        title: 'Analysis',
        items: [
          // Both: the Swarm instance runs its own analyst box (swarm-nexus, Grok) with
          // its own log store; the page is the same and reads the box's firmware notes.
          { label: 'Analyst', to: '/analyst', require: 'analyst', instances: BOTH, desc: 'PX4 flight-log analysis', icon: 'analyst' },
          { label: 'Statistics', to: '/statistics', instances: BOTH, desc: 'Fleet stats & leaderboards', icon: 'stats' },
        ],
      },
      {
        // Expense capture + reconciliation. Everyone reviews their OWN receipts
        // (no gate); Reconcile is financial-only. Lives in Tools (always-visible)
        // rather than Warehouse to avoid a name-clash with parts "Receiving".
        title: 'Expenses',
        items: [
          { label: 'Receipts', to: '/receipts', instances: BOTH, notif: 'receipts', desc: 'Scan & submit expense receipts', icon: 'fin' },
          { label: 'Requests', to: '/requisitions', instances: BOTH, notif: 'requisitions', desc: 'Ask for something to be bought', icon: 'request' },
          { label: 'Reconcile', to: '/reconcile', instances: BOTH, require: 'financial', lock: true, desc: 'Match card charges to receipts', icon: 'fin' },
          // Accounts payable (ERP chunk 2): vendor bills in, matched to POs,
          // confirmed by a human. Financial-family per Mannie's pin 4 — end of
          // section, appended per the audit-branch coordination agreement.
          { label: 'Invoices', to: '/invoices', instances: BOTH, require: 'financial', lock: true, desc: 'Vendor bills, matched to POs', icon: 'fin' },
        ],
      },
    ],
  },
];

/** Reachable only via ⌘K — real routes intentionally kept out of the nav. */
export const EXTRA_DESTINATIONS: ExtraDestination[] = [
  { label: 'Configurations', to: '/configurations', instances: BOTH, crumb: 'Fleet' },
  { label: 'Serial Generator', to: '/serialgen', instances: BOTH, crumb: 'Fleet' },
  { label: 'Batch Print', to: '/batch_print', instances: BOTH, crumb: 'Fleet' },
  { label: 'Monday Inbox', to: '/events/monday-inbox', require: 'hasRole', crumb: 'Shows' },
  { label: 'Tours', to: '/events/tours', require: 'hasRole', crumb: 'Shows' },
  { label: 'Airspace', to: '/pilots/airspace', require: 'poa', crumb: 'Shows' },
  { label: 'RMA Catalog', to: '/rma/catalog', instances: BOTH, crumb: 'Service' },
  // Scan to stock. Reached in practice from the "Scan PO" button on the purchase
  // orders page, next to the box it is about — but it belongs in the palette
  // too, because "scan" is what somebody standing at the bench will type.
  { label: 'Scan PO', to: '/procurement/scan', instances: BOTH, require: 'procurement', crumb: 'Warehouse' },
  // Quote to draft PO. In the palette for the same reason as Scan PO: "quote" is
  // the word somebody with a supplier's email open will type.
  { label: 'Draft PO from a quote', to: '/procurement/quote', instances: BOTH, require: 'procurement', crumb: 'Warehouse' },
  // ── DEMOTED FROM THE MENU, KEPT IN THE PALETTE ────────────────────────────
  // Each of these is a button on the page it belongs to (Catalog, Receiving,
  // Purchase Orders). They stay here because the palette answers a different
  // question from the menu: the menu is "where can I go", the palette is "I know
  // what it is called". Somebody who types "packing" should not have to know it
  // lives behind the Receiving Hub.
  { label: 'Catalog from a document', to: '/parts-catalog/import', instances: BOTH, require: 'procurement', crumb: 'Warehouse' },
  { label: 'Part Families', to: '/admin/part-families', instances: BOTH, require: 'familyEditor', crumb: 'Warehouse' },
  { label: 'Read a packing list', to: '/receiving/packing-list', instances: BOTH, require: 'receivingHub', crumb: 'Warehouse' },
  { label: 'Shipments', to: '/receiving/shipments', instances: BOTH, require: 'receivingHub', crumb: 'Warehouse' },
  { label: 'PO Import History', to: '/procurement/import', instances: BOTH, require: 'procurement', crumb: 'Warehouse' },
  { label: 'API Keys', to: '/admin/apikeys', instances: BOTH, require: 'admin', crumb: 'Admin' },
  { label: 'Scanners', to: '/admin/scanners', instances: BOTH, require: 'admin', crumb: 'Admin' },
  { label: 'Records to reconcile', to: '/admin/reconcile', instances: BOTH, require: 'admin', crumb: 'Admin' },
  { label: 'Flash Operations', to: '/admin/flash-operations', instances: BOTH, require: 'admin', crumb: 'Admin' },
  { label: 'Manufacturing Report', to: '/admin/manufacturing-report', require: 'admin', crumb: 'Admin' },
  // Assets are on both, so the type registry behind them has to be too —
  // otherwise Swarm can never define an asset type.
  { label: 'Asset Types', to: '/admin/asset-types', instances: BOTH, require: 'admin', crumb: 'Admin' },
  { label: 'Vendor Types', to: '/admin/vendor-types', instances: BOTH, require: 'procurement', crumb: 'Warehouse' },
  // What a cataloged thing IS. Both products mint SKUs, so both need the
  // vocabulary those SKUs are spelled from.
  { label: 'Item Kinds', to: '/admin/item-kinds', instances: BOTH, require: 'procurement', crumb: 'Warehouse' },
  { label: 'AI Controls', to: '/admin/ai', require: 'admin', crumb: 'Admin' },
  // Set-once money-control config; lives with the admin cluster, not in the
  // everyday Warehouse menu (Mannie, 2026-07-30).
  { label: 'Approval Settings', to: '/admin/approval-settings', instances: BOTH, require: 'admin', crumb: 'Admin' },
  // Every deployment needs its own account settings.
  { label: 'Settings', to: '/settings', instances: BOTH, crumb: 'You' },
  // Crew shifts and show travel. Swarm has neither.
  // Label only — the ROUTE stays /my-schedule. Renaming it would touch
  // routeInstances, every existing link and everyone's muscle memory for no
  // user-visible gain. The page is now the crew's own shows PLUS the read-only
  // full staff timeline (ff-feedback items 7 + 14), hence the broader name.
  // The producer's editable grid at /schedule keeps the label "Schedule".
  { label: 'Crew Schedule', to: '/my-schedule', require: 'canTravel', crumb: 'You' },
  // Reconcile-workspace side surfaces: reached from the Reconcile library header,
  // kept out of the menu but still findable via the palette. On both, because
  // Reconcile is.
  { label: 'Ledger', to: '/receipts/ledger', instances: BOTH, require: 'financial', crumb: 'Tools' },
  { label: 'Insights', to: '/receipts/insights', instances: BOTH, require: 'financial', crumb: 'Tools' },
];

/** Filter items → drop empty columns → drop empty domains, for a given user. */
export function getVisibleTree(user: unknown): NavDomain[] {
  return NAV_TREE
    .map((domain) => {
      const columns = domain.columns
        .map((col) => ({ ...col, items: col.items.filter((it) => can(it, user)) }))
        .filter((col) => col.items.length > 0);
      // Drop the featured panel when its CTA points somewhere this viewer cannot
      // reach. Previously impossible — a domain only survived if its items did,
      // and the CTA always pointed at one of them. Instance gating breaks that:
      // Swarm keeps Shows for Dispatch/Vehicles while Events is hidden, which
      // would leave a "View events" button linking into a hidden feature.
      const featured =
        domain.featured && columns.some((c) => c.items.some((it) => it.to === domain.featured!.to))
          ? domain.featured
          : undefined;
      const label = domain.instanceLabel?.[INSTANCE] || domain.label;
      return { ...domain, label, featured, columns };
    })
    .filter((domain) => domain.columns.length > 0);
}

/** Flat list of every permitted destination (tree + extras) for the ⌘K palette. */
export function getVisibleDestinations(
  user: unknown,
): Array<{ label: string; to: string; crumb: string; extra: boolean }> {
  const out: Array<{ label: string; to: string; crumb: string; extra: boolean }> = [];
  NAV_TREE.forEach((d) =>
    d.columns.forEach((c) =>
      c.items.forEach((it) => {
        if (can(it, user)) out.push({ label: it.label, to: it.to, crumb: d.label, extra: false });
      }),
    ),
  );
  EXTRA_DESTINATIONS.forEach((e) => {
    if (can(e, user)) out.push({ label: e.label, to: e.to, crumb: e.crumb, extra: true });
  });
  return out;
}

/**
 * Bell keys that have no nav row of their own and belong to one that does.
 * One nav row can carry a single count while the bell splits it into lines that
 * deep-link to different tabs. Without this, the instance filter below drops
 * the bell keys as orphans and they never show.
 */
const NOTIF_PARENTS: Record<string, string> = {
  requisitionApprovals: 'requisitions',
  requisitionsToBuy: 'requisitions',
  workOrderInbox: 'workOrders',
};

/**
 * Does this deployment own the nav item that a notif key belongs to?
 *
 * Alerts roll up into the bell, so an un-gated count for a hidden feature would
 * inflate the badge with work the product cannot even show. Gated here rather
 * than at the fetch, because one fetch can feed several keys.
 */
export function notifAllowedOnInstance(notif: string): boolean {
  const key = NOTIF_PARENTS[notif] ?? notif;
  return NAV_TREE.some((d) =>
    d.columns.some((c) => c.items.some((it) => it.notif === key && instanceAllows(it))),
  );
}

/** Which domain owns a given alert (notif) key — used to glow the source trigger. */
export function domainKeyForNotif(notif: string): string | null {
  const key = NOTIF_PARENTS[notif] ?? notif;
  for (const d of NAV_TREE) {
    for (const c of d.columns) {
      for (const i of c.items) if (i.notif === key) return d.key;
    }
  }
  return null;
}

/**
 * Map a visited pathname to the nav destination it belongs to (longest `to`
 * prefix wins), for the mobile "Jump back in" row. Returns null for paths that
 * aren't a permitted destination (e.g. deep detail routes with no nav home).
 */
export function resolveDestination(path: string, user: unknown): { label: string; to: string } | null {
  let best: { label: string; to: string } | null = null;
  let bestLen = -1;
  for (const d of getVisibleDestinations(user)) {
    if ((path === d.to || path.startsWith(d.to + '/')) && d.to.length > bestLen) {
      best = { label: d.label, to: d.to };
      bestLen = d.to.length;
    }
  }
  return best;
}
